import {
  addNewAnalysisIncident,
  cancelEditingForCurrentLoadedAnalyses,
  clearAnalysis,
  customizeFormat,
  customizeMeasureColor,
  customizeSliceColor,
  deleteAnalysisIncident,
  editAnalysisIncident,
  enableEditingForCurrentLoadedAnalyses,
  fallbackToDefaultState,
  handleResizeAnalysis,
  populateAnalysis,
  populateBuilderInfo,
  renameAnalysis,
  resetAliasReplacement,
  setAnalysisFavorite,
  toggleAllTypeSwitches,
  toggleIncidentCreateModal,
  toggleSingleTypeSwitch,
  updateAliasReplacement,
} from './cases/common';
import {
  setFunnelShowInReportState,
  toggleBounceLabels,
  toggleClusterLabels,
  toggleConversionLabels,
  toggleIsConversionView,
  toggleIsRelativeLabels,
} from './cases/funnel';
import { selectPredictionKind } from './cases/prediction';
import {
  pathSetDisplaySeq,
  pathSetEventsPerStep,
  pathSetMaxSeq,
} from './cases/path';
import {
  resetCentralChartSettings,
  selectChartSettingsSlice,
  selectChartType,
  setChartSettingChartNumberType,
  setChartSettingChurn,
  setChartSettingGeoMapDisplayedMeasureKey,
  setChartSettingGeoMapHeatmap,
  setChartSettingGeoMapPosition,
  setChartSettingMeasure,
  setChartSettingPathNodeCustomization,
  setChartSettingPathObservedNodeId,
  setChartSettingsMeasureVisibility,
  setChartSettingsYAxisPosition,
  setChartSettingYAxisConfig,
  setSingleDayLabel,
  toggleChartSettingsMeasure,
  toggleChartSettingSort,
  toggleChartSettingsSlice,
  toggleChartSettingsTopBreakdowns,
  toggleIsApplyYAxisFormat,
  toggleTopChartSettingsSlices,
} from './cases/chartSettings';
import {
  IChartSettings,
  IExpressionSettings,
  ITableSettings,
} from '../../../components/Chart/interfaces';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAnalysesState } from './types';
import { assertNotNil } from '../../../utils/types';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { isScratchpadAnalysisId } from '../../utils/transientAnalysis';

const initialState: IAnalysesState = {};

export default createSlice({
  name: 'analyses',
  initialState,
  reducers: {
    populateAnalysisAction: populateAnalysis,
    populateBuilderInfoAction: populateBuilderInfo,
    clearAnalysisAction: clearAnalysis,
    customizeFormatAction: customizeFormat,
    handleResizeAnalysisAction: handleResizeAnalysis,
    renameAnalysisAction: renameAnalysis,
    fallbackToDefaultStateAction: fallbackToDefaultState,
    toggleSingleTypeSwitchAction: toggleSingleTypeSwitch,
    toggleAllTypeSwitchesAction: toggleAllTypeSwitches,
    setAnalysisFavoriteAction: setAnalysisFavorite,
    addNewAnalysisIncidentAction: addNewAnalysisIncident,
    editAnalysisIncidentAction: editAnalysisIncident,
    deleteAnalysisIncidentAction: deleteAnalysisIncident,
    toggleIncidentCreateModalAction: toggleIncidentCreateModal,
    updateAliasReplacementAction: updateAliasReplacement,
    resetAliasReplacementAction: resetAliasReplacement,
    selectPredictionKindAction: selectPredictionKind,
    pathSetDisplaySeqAction: pathSetDisplaySeq,
    pathSetMaxSeqAction: pathSetMaxSeq,
    pathSetEventsPerStepAction: pathSetEventsPerStep,
    enableEditingForCurrentLoadedAnalysesAction:
      enableEditingForCurrentLoadedAnalyses,
    cancelEditingForCurrentLoadedAnalysesAction:
      cancelEditingForCurrentLoadedAnalyses,
    setFunnelShowInReportStateAction: setFunnelShowInReportState,
    toggleIsConversionViewAction: toggleIsConversionView,
    toggleConversionLabelsAction: toggleConversionLabels,
    toggleBounceLabelsAction: toggleBounceLabels,
    toggleIsRelativeLabelsAction: toggleIsRelativeLabels,
    toggleClusterLabelsAction: toggleClusterLabels,
    toggleChartSettingsMeasureAction: toggleChartSettingsMeasure,
    setChartSettingMeasureAction: setChartSettingMeasure,
    toggleChartSettingsSliceAction: toggleChartSettingsSlice,
    toggleTopChartSettingsSlicesAction: toggleTopChartSettingsSlices,
    selectChartSettingsSliceAction: selectChartSettingsSlice,
    setChartSettingGeoMapPositionAction: setChartSettingGeoMapPosition,
    setChartSettingGeoMapHeatmapAction: setChartSettingGeoMapHeatmap,
    setChartSettingGeoMapDisplayedMeasureKeyAction:
      setChartSettingGeoMapDisplayedMeasureKey,
    selectChartTypeAction: selectChartType,
    setSingleDayLabelAction: setSingleDayLabel,
    toggleChartSettingSortAction: toggleChartSettingSort,
    toggleChartSettingsTopBreakdownsAction: toggleChartSettingsTopBreakdowns,
    setChartSettingChurnAction: setChartSettingChurn,
    setChartSettingChartNumberTypeAction: setChartSettingChartNumberType,
    setChartSettingsYAxisPositionAction: setChartSettingsYAxisPosition,
    setChartSettingPathNodeCustomizationAction:
      setChartSettingPathNodeCustomization,
    setChartSettingPathObservedNodeIdAction: setChartSettingPathObservedNodeId,
    setChartSettingsMeasureVisibility,
    resetCentralChartSettingsAction: resetCentralChartSettings,
    toggleIsApplyYAxisFormatAction: toggleIsApplyYAxisFormat,
    setChartSettingYAxisConfigAction: setChartSettingYAxisConfig,
    clearAllAnalysesAction: (state) => {
      // Keep transient analyses with IDs ending in "_scratchpad"
      // and remove all other analyses
      Object.keys(state).forEach((analysisId) => {
        if (!isScratchpadAnalysisId(analysisId)) {
          delete state[analysisId];
        }
      });
    },
    clearAllScratchpadAnalysesAction: (state) => {
      // Remove only analyses with IDs ending in "_scratchpad"
      Object.keys(state).forEach((analysisId) => {
        if (isScratchpadAnalysisId(analysisId)) {
          delete state[analysisId];
        }
      });
    },
    setChartSettingsExpressionSettingsAction: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        expressionSettings: IExpressionSettings;
      }>,
    ) => {
      const { analysisId, expressionSettings } = action.payload;

      const currAnalysis = draft[analysisId];
      const chartSettings = currAnalysis.settings.chartSettings;

      chartSettings.expressionSettings = expressionSettings;
    },
    setChartSettingsTableSettingsAction: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        tableSettings: Partial<ITableSettings>;
      }>,
    ) => {
      const { analysisId, tableSettings } = action.payload;

      const currAnalysis = draft[analysisId];
      const chartSettings = currAnalysis.settings.chartSettings;

      chartSettings.tableSettings = {
        ...chartSettings.tableSettings,
        ...tableSettings,
      };

      // since we are updating the table column widths after the snapshot has been taken,
      // we need to update the chartSettingsSnapshot.tableSettings to match the currentChartSettings.tableSettings
      if (
        currAnalysis.settings.chartSettingsSnapshot?.tableSettings &&
        !currAnalysis.settings.chartSettingsSnapshot.tableSettings
          .columnWidths &&
        tableSettings.columnWidths
      ) {
        currAnalysis.settings.chartSettingsSnapshot.tableSettings.columnWidths =
          tableSettings.columnWidths;
      }
    },
    setInspectSubjectIdAction: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        subjectId: string | null;
      }>,
    ) => {
      const { subjectId, analysisId } = action.payload;
      draft[analysisId].settings.inspectSubjectId = subjectId;
    },
    customizeMeasureColorAction: customizeMeasureColor,
    customizeSliceColorAction: customizeSliceColor,
    updateChartSettings: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        chartSettings: IChartSettings;
      }>,
    ) => {
      const { analysisId, chartSettings } = action.payload;
      const currAnalysis = draft[analysisId];
      assertNotNil(analysisId);

      currAnalysis.settings.chartSettingsSnapshot = chartSettings;

      // update the current chart settings to match the initial chart settings
      currAnalysis.settings.chartSettings = chartSettings;

      // same for aliases
      const chartData = currAnalysis.chartData;
      if (chartData && 'aliasReplacements' in chartData) {
        chartData.aliasReplacementsSnapshot = chartData.aliasReplacements;
      }
    },
    updateAnalysisSummaryRichText: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        richText: MaybeNil<string>;
      }>,
    ) => {
      const { analysisId, richText } = action.payload;
      assertNotNil(analysisId);
      const currAnalysis = draft[analysisId];
      const showInDashboard =
        currAnalysis?.metadata?.richTextSummary?.showInDashboard ?? true;
      if (currAnalysis.metadata) {
        currAnalysis.metadata.richTextSummary = isNil(richText)
          ? null
          : {
              showInDashboard,
              richText: {
                html: richText,
              },
            };
      }
    },
    updateAnalysisSummaryShowInDashboard: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        showInDashboard: boolean;
      }>,
    ) => {
      const { analysisId, showInDashboard } = action.payload;
      assertNotNil(analysisId);
      const currAnalysis = draft[analysisId];
      if (currAnalysis.metadata?.richTextSummary) {
        currAnalysis.metadata.richTextSummary.showInDashboard = showInDashboard;
      }
    },
  },
});
